import { Command } from 'cmdk'
import { Check, ChevronDown, Loader2, Search } from 'lucide-react'
import { useRef, useState, type MouseEvent, type ReactNode } from 'react'
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

export type SelectOption<V extends string = string> = {
  value: V
  label: string
  description?: string
  icon?: ReactNode
  disabled?: boolean
  keywords?: string[]
}

const SIZES = {
  lg: 'h-10 px-3.5 text-sm',
  default: 'h-9 px-3 text-sm',
  sm: 'h-8 px-2.5 text-xs',
  xs: 'h-7 px-2 text-[11px]',
} as const

const SEARCH_THRESHOLD = 8

export type SelectProps<V extends string = string> = {
  value: V | '' | null | undefined
  onChange: (value: V) => void
  options: readonly SelectOption<V>[]
  placeholder?: string
  searchable?: boolean
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
  loading?: boolean
  invalid?: boolean
  size?: keyof typeof SIZES
  id?: string
  name?: string
  className?: string
  contentClassName?: string
  align?: 'start' | 'center' | 'end'
  preserveFocus?: boolean
  onBlur?: () => void
  /** Listede olmayan değeri serbest metin olarak kabul eder (arama → "“x” kullan"). */
  creatable?: boolean
  'aria-label'?: string
  'aria-describedby'?: string
}

const matches = (search: string, keywords: string[] = []) =>
  keywords.join(' ').toLocaleLowerCase('tr').includes(search.trim().toLocaleLowerCase('tr')) ? 1 : 0

export function Select<V extends string = string>({
  value,
  onChange,
  options,
  placeholder,
  searchable,
  searchPlaceholder,
  emptyText,
  disabled,
  loading,
  invalid,
  size = 'default',
  id,
  name,
  className,
  contentClassName,
  align = 'start',
  preserveFocus,
  onBlur,
  creatable,
  ...aria
}: SelectProps<V>) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const commandRef = useRef<HTMLDivElement>(null)
  const selectedIndex = options.findIndex((o) => o.value === value)
  const custom = creatable && value && selectedIndex < 0 ? { value: value as V, label: value } : undefined
  const selected: SelectOption<V> | undefined = options[selectedIndex] ?? custom
  const showSearch = creatable || (searchable ?? options.length > SEARCH_THRESHOLD)
  const typed = query.trim()
  const canCreate = !!creatable && !!typed && !options.some((o) => o.label.toLocaleLowerCase('tr') === typed.toLocaleLowerCase('tr'))
  const keepFocus = preserveFocus ? (e: MouseEvent) => e.preventDefault() : undefined

  const changeOpen = (next: boolean) => {
    setOpen(next)
    if (!next) {
      setQuery('')
      onBlur?.()
    }
  }

  return (
    <Popover open={open} onOpenChange={changeOpen} modal={!preserveFocus}>
      <PopoverTrigger asChild disabled={disabled || loading}>
        <button
          type="button"
          id={id}
          name={name}
          role="combobox"
          aria-expanded={open}
          aria-busy={loading || undefined}
          aria-invalid={invalid || undefined}
          onMouseDown={keepFocus}
          className={cn(
            'flex w-full min-w-0 items-center justify-between gap-2 rounded-md border border-input bg-card text-left shadow-xs transition-colors hover:border-ring/50 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive data-[state=open]:border-ring data-[state=open]:ring-2 data-[state=open]:ring-ring/30',
            SIZES[size],
            className,
          )}
          {...aria}
        >
          <span className={cn('flex min-w-0 items-center gap-2', !selected && 'text-muted-foreground')}>
            {selected?.icon}
            <span className="truncate">{selected ? selected.label : (placeholder ?? t('common.select'))}</span>
          </span>
          {loading ? (
            <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" aria-hidden />
          ) : (
            <ChevronDown className={cn('size-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} aria-hidden />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className={cn('min-w-44 p-0', contentClassName)}
        onMouseDown={keepFocus}
        onOpenAutoFocus={(e) => {
          if (preserveFocus || !showSearch) e.preventDefault()
          if (!preserveFocus && !showSearch) commandRef.current?.focus()
        }}
        onCloseAutoFocus={preserveFocus ? (e) => e.preventDefault() : undefined}
      >
        <Command
          ref={commandRef}
          tabIndex={-1}
          loop
          label={aria['aria-label'] ?? placeholder}
          defaultValue={selectedIndex >= 0 ? `opt-${selectedIndex}` : undefined}
          filter={(_value, search, keywords) => matches(search, keywords)}
          className="outline-none"
        >
          {showSearch ? (
            <div className="flex items-center gap-2 border-b px-3">
              <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              <Command.Input
                value={query}
                onValueChange={setQuery}
                placeholder={searchPlaceholder ?? t('common.selectSearch')}
                className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
          ) : null}
          <Command.List className="scrollbar-none max-h-64 overflow-y-auto p-1">
            {canCreate ? null : (
              <Command.Empty className="px-3 py-6 text-center text-sm text-muted-foreground">{emptyText ?? t('common.noOptions')}</Command.Empty>
            )}
            {canCreate ? (
              <Command.Item
                value="opt-create"
                keywords={[typed]}
                onSelect={() => {
                  onChange(typed as V)
                  changeOpen(false)
                }}
                className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-sm outline-none data-[selected=true]:bg-muted"
              >
                <span className="min-w-0 flex-1 truncate">{t('common.useValue', { value: typed })}</span>
              </Command.Item>
            ) : null}
            {options.map((o, i) => (
              <Command.Item
                key={`opt-${i}`}
                value={`opt-${i}`}
                keywords={[o.label, ...(o.keywords ?? [])]}
                disabled={o.disabled}
                onSelect={() => {
                  if (o.value !== value) onChange(o.value)
                  changeOpen(false)
                }}
                className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-sm outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-muted data-[disabled=true]:opacity-50"
              >
                {o.icon}
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{o.label}</span>
                  {o.description ? <span className="block truncate text-xs text-muted-foreground">{o.description}</span> : null}
                </span>
                <Check className={cn('size-4 shrink-0 text-primary', o.value === value ? 'opacity-100' : 'opacity-0')} aria-hidden />
              </Command.Item>
            ))}
          </Command.List>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export type FormSelectProps<T extends FieldValues, C, TT extends FieldValues, V extends string> = Omit<
  SelectProps<V>,
  'value' | 'onChange' | 'onBlur' | 'name' | 'invalid'
> & {
  control: Control<T, C, TT>
  name: FieldPath<T>
  onValueChange?: (value: V) => void
}

export function FormSelect<T extends FieldValues, C = unknown, TT extends FieldValues = T, V extends string = string>({
  control,
  name,
  onValueChange,
  ...props
}: FormSelectProps<T, C, TT, V>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Select<V>
          {...props}
          name={field.name}
          value={field.value as V}
          invalid={!!fieldState.error}
          onBlur={field.onBlur}
          onChange={(v) => {
            field.onChange(v)
            onValueChange?.(v)
          }}
        />
      )}
    />
  )
}
